// Batch import. Legacy: processBatchFile/fetchGoogleSheet/parseBatchPaste (L7559-7668).
// Uses SheetJS (xlsx) exactly as the legacy editor did. (Phase 3 note: npm xlsx is
// pinned to 0.18.5; the legacy CDN build was 0.20.1 — switch to the SheetJS registry
// build before production for the security fixes. API is identical for our usage.)
import * as XLSX from 'xlsx'
import type { BatchRow } from './../types'

export interface ParsedBatch {
  headers: string[]
  rows: BatchRow[]
}

function workbookToBatch(wb: XLSX.WorkBook): ParsedBatch {
  const sheet = wb.Sheets[wb.SheetNames[0]]
  // raw:false = use each cell's FORMATTED text (SheetJS `w`), not the stored value.
  // Excel silently turns entries like "1/001" into a date stored as serial 36892;
  // with raw values that serial is what printed on labels (customer report,
  // 2026-08-24). What the user sees in their spreadsheet is what should print.
  // Long numbers in "General" format (barcodes, SKUs, phone numbers) get a
  // SHORT display text from SheetJS ("5.90123E+12" for 5901234123457) — a
  // barcode then encodes the wrong value (customer report, 2026-10-21).
  // Use the full number when the display text is that short form; every other
  // cell (dates, currency, "1/001") keeps its displayed text as before.
  for (const addr of Object.keys(sheet)) {
    if (addr[0] === '!') continue
    const c = sheet[addr] as XLSX.CellObject
    // Only Excel's scientific short form ("5.90123E+12"): dates and other
    // formatted numbers have no format code after reading but a different text.
    if (c && c.t === 'n' && typeof c.v === 'number' && Number.isFinite(c.v) && typeof c.w === 'string' && /^-?\d(\.\d+)?E[+-]\d+$/i.test(c.w.trim())) {
      c.w = Number.isInteger(c.v) && Math.abs(c.v) <= Number.MAX_SAFE_INTEGER ? BigInt(c.v).toString() : String(c.v)
    }
  }
  const rows = XLSX.utils.sheet_to_json<BatchRow>(sheet, { defval: '', raw: false })
  if (rows.length === 0) throw new Error('File is empty or has no data rows')
  return { headers: Object.keys(rows[0]), rows }
}

/** Parse CSV/TSV/pasted text. raw:true at READ time: SheetJS's text parser
 *  otherwise auto-interprets values like "1/001" as dates (→ serial 36892) while
 *  parsing, before any output formatting can help. Text sources are verbatim. */
export function parseDelimitedText(text: string): ParsedBatch {
  return workbookToBatch(XLSX.read(text, { type: 'string', raw: true }))
}

/** Parse an .xlsx/.xls ArrayBuffer. */
export function parseSpreadsheetBuffer(buf: ArrayBuffer): ParsedBatch {
  return workbookToBatch(XLSX.read(new Uint8Array(buf), { type: 'array' }))
}

/** Parse a dropped/selected File (csv/tsv read as text, xlsx as binary). */
export function parseBatchFile(file: File): Promise<ParsedBatch> {
  const isText = /\.(csv|tsv)$/i.test(file.name)
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error)
    reader.onload = () => {
      try {
        resolve(isText
          ? parseDelimitedText(typeof reader.result === 'string' ? reader.result : new TextDecoder().decode(reader.result as ArrayBuffer))
          : parseSpreadsheetBuffer(reader.result as ArrayBuffer))
      } catch (err) {
        reject(err)
      }
    }
    if (isText) reader.readAsText(file)
    else reader.readAsArrayBuffer(file)
  })
}

/** Build the Google Sheets CSV export URL from a share URL (legacy fetchGoogleSheet). */
export function googleSheetCsvUrl(url: string): string | null {
  const id = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/)
  if (!id) return null
  const gid = url.match(/[#&?]gid=(\d+)/)?.[1] ?? '0'
  return `https://docs.google.com/spreadsheets/d/${id[1]}/export?format=csv&gid=${gid}`
}

export async function fetchGoogleSheet(url: string): Promise<ParsedBatch> {
  const csvUrl = googleSheetCsvUrl(url)
  if (!csvUrl) throw new Error('Invalid Google Sheets URL')
  const res = await fetch(csvUrl)
  if (!res.ok) throw new Error('Sheet not accessible — set sharing to "Anyone with the link can view".')
  return parseDelimitedText(await res.text())
}
